import { useEffect, useMemo } from "react";

import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { clabeDigits } from "@/shared/utils/clabe";

import type { SpeiSendRecipient } from "../../utils/walletSend";
import { EMPTY_SPEI_VALUES, speiRecipientSchema, type SpeiRecipientValues } from "./sendRecipients.schema";

export interface SpeiRecipientFormProps {
  defaults: SpeiRecipientValues;
  /** Every edit: the raw values, and the recipient once they are complete. */
  onChange: (values: SpeiRecipientValues, recipient: SpeiSendRecipient | null) => void;
}

const rfcInput = (raw: string) =>
  raw
    .toUpperCase()
    .replace(/[^A-ZÑ&0-9]/g, "")
    .slice(0, 13);

/** A CLABE and its holder (`SpeiDestinationDto`). The RFC is optional; a 12-character one marks a company. */
export function SpeiRecipientForm({ defaults, onChange }: SpeiRecipientFormProps) {
  const { t } = useTranslations("dispersions");
  const schema = useMemo(
    () =>
      speiRecipientSchema({
        clabe: t("error_clabe_invalid"),
        clabeCheck: t("send_clabe_check_error"),
        name: t("error_recipient_name_required"),
        rfc: t("send_rfc_error"),
      }),
    [t],
  );
  const form = useZodForm(schema, defaults);

  useEffect(() => {
    const report = (values: Partial<SpeiRecipientValues>) => {
      const full = { ...EMPTY_SPEI_VALUES, ...values } as SpeiRecipientValues;
      const parsed = schema.safeParse(full);
      onChange(
        full,
        parsed.success
          ? {
              rail: "SPEI",
              clabe: parsed.data.clabe,
              beneficiaryName: parsed.data.beneficiaryName,
              rfc: parsed.data.rfc || null,
            }
          : null,
      );
    };
    report(form.getValues());
    const subscription = form.watch((values) => report(values as Partial<SpeiRecipientValues>));
    return () => subscription.unsubscribe();
  }, [form, schema, onChange]);

  return (
    <ColocaForm form={form} onSubmit={() => undefined} className="space-y-4">
      <ColocaInputField
        name="beneficiaryName"
        label={t("send_name_label")}
        placeholder={t("send_name_placeholder")}
        maxLength={120}
        autoComplete="off"
        required
      />
      <ColocaInputField
        name="clabe"
        label={t("step3_clabe_label")}
        placeholder={t("step2_clabe_placeholder")}
        description={t("step2_clabe_helper")}
        inputMode="numeric"
        maxLength={18}
        autoComplete="off"
        transform={clabeDigits}
        required
      />
      <ColocaInputField
        name="rfc"
        label={t("send_rfc_label")}
        placeholder={t("send_rfc_placeholder")}
        description={t("send_rfc_helper")}
        maxLength={13}
        autoComplete="off"
        transform={rfcInput}
      />
    </ColocaForm>
  );
}
